import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table.jsx";
import { useEnalysisOptimizationContext } from "@/contexts/EnalysisOptimizationContext.jsx";
import {
  CYLINDER_DETAIL_ROWS,
  REPORT_DETAIL_ROWS,
  PERFORMANCE_FLAG_ROWS,
  BASIC_CYLINDER_ROWS,
  formatValue,
  getReportDetailsColumns,
  WARNING_LEVEL_NONE,
  WARNING_LEVEL_SEVERE,
  WARNING_LEVEL_MODERATE,
} from "@/lib/reports-utils.js";
import { cn } from "@/lib/utils.js";
import { cardStyles } from "@/lib/status-styles.js";
import { getStatusColorClasses } from "@/lib/status-utils.js";
import { useState, useEffect, useCallback, useMemo } from "react";
import { Zap } from "lucide-react";

const REPORT_FLAG_DETAIL_ROWS = REPORT_DETAIL_ROWS.filter((r) => r.warningFlag);

const cylinderTableHeadClass = cn(
  cardStyles.tableHead,
  "cursor-default select-auto hover:!bg-muted text-right"
);

const cylinderDataRowClass = (index) =>
  cn(
    "border-t border-border transition-colors hover:bg-muted dark:hover:bg-white/10",
    index % 2 === 0 ? "bg-card" : "bg-muted/50"
  );

const cylinderTableCellClass = "text-foreground font-light py-2 px-4 text-right";

const sectionShellClass =
  "rounded-lg border border-border bg-card overflow-hidden shadow-sm";

const sectionHeaderClass = cn(cardStyles.header, "rounded-none rounded-t-lg");

const tableHeaderRowClass = cn(cardStyles.tableRow, "hover:bg-muted");

/** Tones mirror the shared status pills (Offline for severe, Danger for moderate). */
const PILL_TONE_CLASS = {
  severe: getStatusColorClasses("Offline"),
  moderate: getStatusColorClasses("Danger"),
};

function rowLabelWithUnit(row) {
  return row.unit ? `${row.label} (${row.unit})` : row.label;
}

function warningClassForLevel(warningLevel) {
  if (warningLevel === WARNING_LEVEL_SEVERE)
    return "text-red-600 font-medium dark:text-red-400";
  if (warningLevel === WARNING_LEVEL_MODERATE)
    return "text-orange-600 font-medium dark:text-orange-400";
  return "";
}

function CylinderSummarySection({ title, headingClassName, children }) {
  return (
    <div className={sectionShellClass}>
      <div className={sectionHeaderClass}>
        <h3
          className={cn("text-lg font-light text-foreground", headingClassName)}>
          {title}
        </h3>
      </div>
      <div className='overflow-x-auto bg-card'>{children}</div>
    </div>
  );
}

function BasicTableHead() {
  return (
    <TableHeader className={cardStyles.tableHeader}>
      <TableRow className={tableHeaderRowClass}>
        {BASIC_CYLINDER_ROWS.map((row) => (
          <TableHead key={row.key} className={cylinderTableHeadClass}>
            {rowLabelWithUnit(row)}
          </TableHead>
        ))}
      </TableRow>
    </TableHeader>
  );
}

/**
 * Cylinder Summary tab: table of fieldDataDetail + reportDetail metrics by Stage/Throw.
 * Uses cached fieldData from EnalysisOptimizationContext (no local fetch).
 */
export default function OptimizationCylinderSummary({ compNum, compId }) {
  const { getOptimizationData } = useEnalysisOptimizationContext();
  const { report, warningFlags } = getOptimizationData(compNum, compId);

  const [columns, setColumns] = useState(() => getReportDetailsColumns(report));
  const [totalSevereFlags, setTotalSevereFlags] = useState(0);
  const [totalModerateFlags, setTotalModerateFlags] = useState(0);
  const [avgBlowby, setAvgBlowby] = useState("");
  const [totalHp, setTotalHp] = useState(0);
  const [totalFlow, setTotalFlow] = useState(0);

  useEffect(() => {
    setColumns(getReportDetailsColumns(report));
  }, [report]);

  const flagDetailList = warningFlags?.fieldData?.fieldDataDetail;

  const countFlags = useCallback(
    (stage, throwNum, warningLevel) => {
      if (!Array.isArray(flagDetailList)) return 0;
      const detailFlags = flagDetailList.find(
        (f) => f.stageNumber === stage && f.throwNumber === throwNum
      );
      if (!detailFlags) return 0;
      return REPORT_FLAG_DETAIL_ROWS.filter(
        (row) => detailFlags[row.warningFlag] === warningLevel
      ).length;
    },
    [flagDetailList]
  );

  const getBlowbyWarningLevel = useCallback(
    (col) => {
      if (!Array.isArray(flagDetailList)) return WARNING_LEVEL_NONE;
      const detailFlags = flagDetailList.find(
        (f) =>
          f.stageNumber === col.detail.stage
          && f.throwNumber === col.detail.throw
      );
      return detailFlags?.blowbyWarningLevel ?? WARNING_LEVEL_NONE;
    },
    [flagDetailList]
  );

  const getDetailFlags = useCallback(
    (col) =>
      flagDetailList?.find(
        (flag) =>
          flag.stageNumber === col.detail.stage
          && flag.throwNumber === col.detail.throw
      ),
    [flagDetailList]
  );

  useEffect(() => {
    if (columns.length === 0) {
      setTotalSevereFlags(0);
      setTotalModerateFlags(0);
      setAvgBlowby("");
      setTotalHp(0);
      setTotalFlow(0);
      return;
    }
    let blowbySum = 0;
    let blowbyCount = 0;
    let severe = 0;
    let moderate = 0;
    let hp = 0;
    let flow = 0;
    columns.forEach((col) => {
      const { cylinder, reportDetail } = col.detail;
      if (
        !cylinder.isDummyCylinder
        && reportDetail.horsepowerCylinder != null
      ) {
        hp += Number(reportDetail.horsepowerCylinder);
      }
      if (
        !cylinder.isDummyCylinder
        && reportDetail.cylinderFlowMMSCFD != null
      ) {
        flow += Number(reportDetail.cylinderFlowMMSCFD);
      }
      if (reportDetail.blowby != null) {
        blowbySum += reportDetail.blowby;
        blowbyCount += 1;
      }
      severe += countFlags(
        col.detail.stage,
        col.detail.throw,
        WARNING_LEVEL_SEVERE
      );
      moderate += countFlags(
        col.detail.stage,
        col.detail.throw,
        WARNING_LEVEL_MODERATE
      );
    });
    setTotalSevereFlags(severe);
    setTotalModerateFlags(moderate);
    setAvgBlowby(
      blowbyCount > 0 ? formatValue(blowbySum / blowbyCount, 1) : ""
    );
    setTotalHp(hp);
    setTotalFlow(flow);
  }, [columns, countFlags]);

  const detailedTableHead = useMemo(
    () => (
      <TableHeader className={cardStyles.tableHeader}>
        <TableRow className={tableHeaderRowClass}>
          <TableHead className={cn(cylinderTableHeadClass, "w-[240px]")}>
            Cylinder
          </TableHead>
          {columns.map((col) => (
            <TableHead
              key={col.key}
              className={cn(cylinderTableHeadClass, "min-w-[100px]")}>
              {col.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
    ),
    [columns]
  );

  const renderDetailRow = useCallback(
    (row, variant, rowIndex) => (
      <TableRow key={row.key} className={cylinderDataRowClass(rowIndex)}>
        <TableCell
          className={cn(cylinderTableCellClass, "font-medium text-foreground")}>
          {rowLabelWithUnit(row)}
        </TableCell>
        {columns.map((col) => {
          let warningLevel = WARNING_LEVEL_NONE;
          if (variant !== "cylinder") {
            const detailFlags = getDetailFlags(col);
            warningLevel = row.warningFlag
              ? (detailFlags?.[row.warningFlag] ?? WARNING_LEVEL_NONE)
              : WARNING_LEVEL_NONE;
          }
          const rawValue =
            variant === "performance"
              ? row.getValue(col, warningLevel)
              : row.getValue(col, col.detail);
          const display =
            variant === "performance" && rawValue === false
              ? ""
              : formatValue(rawValue, row.decimals);
          const warningClass =
            variant === "cylinder" ? "" : warningClassForLevel(warningLevel);
          return (
            <TableCell
              key={col.key}
              className={cn(cylinderTableCellClass, warningClass)}>
              {display}
            </TableCell>
          );
        })}
      </TableRow>
    ),
    [columns, getDetailFlags]
  );

  if (report == null) {
    return (
      <div className='rounded-lg border border-border bg-muted px-6 py-12 text-center'>
        <p className='font-medium text-foreground'>No reports for this asset</p>
        <p className='mt-2 text-sm text-muted-foreground'>
          Cylinder performance data appears when at least one report is
          available for this unit.
        </p>
      </div>
    );
  }

  if (columns.length === 0) {
    return (
      <div className='rounded-lg border border-border bg-muted px-4 py-12 text-center text-muted-foreground'>
        Field data could not be loaded for this report. If issue persists after
        refreshing, please contact support.
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      <CylinderSummarySection
        headingClassName='flex items-center gap-2'
        title={
          <>
            <Zap className='h-5 w-5 text-muted-foreground' aria-hidden />
            Cylinder Performance - Basic
          </>
        }>
        <Table className='w-full'>
          <BasicTableHead />
          <TableBody>
            {columns.map((col, index) => (
              <TableRow key={col.key} className={cylinderDataRowClass(index)}>
                {BASIC_CYLINDER_ROWS.map((row) => {
                  let rawValue = row.getValue(col, col.detail);
                  if (row.key === "flagsSevere") {
                    rawValue = countFlags(
                      col.detail.stage,
                      col.detail.throw,
                      WARNING_LEVEL_SEVERE
                    );
                  } else if (row.key === "flagsModerate") {
                    rawValue = countFlags(
                      col.detail.stage,
                      col.detail.throw,
                      WARNING_LEVEL_MODERATE
                    );
                  }
                  const value =
                    row.decimals != null && rawValue !== "n/a"
                      ? formatValue(rawValue, row.decimals)
                      : rawValue;
                  const isBlowby = row.warningFlag != null;
                  const blowbyLevel = isBlowby
                    ? getBlowbyWarningLevel(col)
                    : WARNING_LEVEL_NONE;
                  const blowbySevere = blowbyLevel === WARNING_LEVEL_SEVERE;
                  const blowbyWarning = blowbyLevel === WARNING_LEVEL_MODERATE;
                  const showPill = row.hasPillStyling;
                  const pillClass =
                    PILL_TONE_CLASS[row.pillTone] ?? PILL_TONE_CLASS.moderate;
                  return (
                    <TableCell
                      key={row.key}
                      className={cn(
                        cylinderTableCellClass,
                        blowbySevere &&
                          "text-red-600 font-medium dark:text-red-400",
                        blowbyWarning &&
                          "text-orange-600 font-medium dark:text-orange-400"
                      )}>
                      {showPill ? (
                        <span
                          className={cn(
                            "inline-flex rounded-full border px-2 py-0.5",
                            pillClass
                          )}>
                          {value}
                        </span>
                      ) : (
                        value
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
            <TableRow
              className={cn(
                "border-t-2 border-border transition-colors hover:bg-muted dark:hover:bg-white/10",
                columns.length % 2 === 0 ? "bg-card" : "bg-muted/50"
              )}>
              {BASIC_CYLINDER_ROWS.map((row, idx) => {
                const isFirst = idx === 0;
                let totalValue = "";
                if (row.isSummable) {
                  switch (row.key) {
                    case "hp":
                      totalValue = formatValue(totalHp, row.decimals);
                      break;
                    case "flow":
                      totalValue = formatValue(totalFlow, row.decimals);
                      break;
                    case "blowby":
                      totalValue = formatValue(avgBlowby, row.decimals);
                      break;
                    case "flagsSevere":
                      if (!row.applyPillStylingInTotal) {
                        totalValue = totalSevereFlags;
                      }
                      break;
                    case "flagsModerate":
                      if (!row.applyPillStylingInTotal) {
                        totalValue = totalModerateFlags;
                      }
                      break;
                    default:
                      break;
                  }
                }
                return (
                  <TableCell
                    key={row.key}
                    className={cn(cylinderTableCellClass, "font-semibold")}>
                    {isFirst ? "Total / Avg" : totalValue}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableBody>
        </Table>
      </CylinderSummarySection>

      <CylinderSummarySection title='Cylinder Performance - Detailed'>
        <Table className='w-full'>
          {detailedTableHead}
          <TableBody>
            {CYLINDER_DETAIL_ROWS.map((row, index) =>
              renderDetailRow(row, "cylinder", index)
            )}
            {REPORT_DETAIL_ROWS.map((row, index) =>
              renderDetailRow(
                row,
                "report",
                CYLINDER_DETAIL_ROWS.length + index
              )
            )}
          </TableBody>
        </Table>
      </CylinderSummarySection>

      <CylinderSummarySection title='Performance Flags'>
        <Table className='w-full'>
          {detailedTableHead}
          <TableBody>
            {PERFORMANCE_FLAG_ROWS.map((row, index) =>
              renderDetailRow(row, "performance", index)
            )}
          </TableBody>
        </Table>
      </CylinderSummarySection>
    </div>
  );
}
